/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { OrgUnitContact, OrgUnitLocation, StoreHours } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
// import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { buildStoreHours, IHoursDisplayInfo, secondsToTime } from './date-time-helpers';
import { IStoreSelectorLocationLineItemProps, IStoreSelectorLocationLineItemResources } from '../../find-store-worldwide';

// /**
//  * Store Locator line item resources.
//  */
// export interface IStoreSelectorLocationLineItemResources {
//     storeHoursHeader: string;
//     closedText: string;
//     storeLabelRetailer: string;
//     storeLabelVenchi: string;
//     goToStoreWebPage: string;

//     days: {
//         monday: string;
//         tuesday: string;
//         wednesday: string;
//         thursday: string;
//         friday: string;
//         saturday: string;
//         sunday: string;
//         mondayFull: string;
//         tuesdayFull: string;
//         wednesdayFull: string;
//         thursdayFull: string;
//         fridayFull: string;
//         saturdayFull: string;
//         sundayFull: string;
//     };
// }

// /**
//  * Store Locator line item props.
//  */
// export interface IStoreSelectorLocationLineItemProps {
//     location: OrgUnitLocation;
//     storeHours?: StoreHours;

//     // hideStockStatus?: boolean;
//     // isInStock?: boolean;
//     // isCurrentLocation?: boolean;

//     resources: IStoreSelectorLocationLineItemResources;
//     // stockStatusLabel?: string;
//     // storeLocatorView?: boolean;
//     // isPreferredStoreEnabled?: boolean;
//     index: string;
//     // preferredStoreLocationId: string | null;
//     // storePickUpOptionList?: DeliveryOption[];
//     // productPickUpOptionList?: DeliveryOption[];
//     // filteredPickupMode?: string;
//     // shouldShowIndex?: boolean;
//     baseUrl?: string;
//     shouldShowStorePageLinks?: boolean;
//     storePageLinksCountries?: string[];
//     baseClassName?: string;

//     /**
//      * The telemetry content.
//      */
//     telemetryContent?: ITelemetryContent;
//     preferredDistanceUnit: string;
//     isLocationDisabled?: boolean;
//     // handlers: {
//     //     onSelected(location: OrgUnitLocation, deliveryMode?: string): void;
//     //     onSetAsPreferredStore(locationId: string | undefined): void;
//     //     onRemovePreferredStore(): void;
//     // };
// }

const hasStoreHoursInfo = (storeHours: StoreHours | undefined, location: OrgUnitLocation): boolean => {
    if (storeHours?.RegularStoreHours || (location.OpenFrom && location.OpenTo)) {
        return true;
    }
    return false;
};

/**
 * Function to render store hours.
 * @param storeHours - Store Hours object.
 * @param location - Store Unit.
 * @param resources - Resources.
 * @returns - Jsx element.
 */
export const renderStoreHours = (
    storeHours: StoreHours | undefined,
    location: OrgUnitLocation,
    resources: IStoreSelectorLocationLineItemResources
): JSX.Element | null => {
    // First, use the store hours. This will allow listing day by day houts
    if (storeHours?.RegularStoreHours) {
        const storeHoursList: IHoursDisplayInfo[] = buildStoreHours(storeHours.RegularStoreHours, resources);
        return (
            <table className='ms-store-select-venchi__store-hours-details-group' aria-label={resources.storeHoursHeader}>
                <thead className='sr-only'>
                    <tr>
                        {
                            //TODO LOCALIZE
                        }
                        <th scope='col'>Day</th>
                        <th scope='col'>Hours</th>
                    </tr>
                </thead>
                <tbody>
                    {storeHoursList.map(displayTime => (
                        <tr className='ms-store-select-venchi__store-hours-details' key={JSON.stringify(displayTime)}>
                            <td className='ms-store-select-venchi__store-hours-day'>
                                {displayTime.endDayShort ? (
                                    <>
                                        <abbr title={displayTime.startDayFull}>{displayTime.startDayShort}</abbr>–
                                        <abbr title={displayTime.endDayFull}>{displayTime.endDayShort}</abbr>
                                    </>
                                ) : (
                                    <abbr title={displayTime.startDayFull}>{displayTime.startDayShort}</abbr>
                                )}
                            </td>
                            <td className='ms-store-select-venchi__store-hours-time'>{displayTime.timeRange}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
        // return (
        //     <div className='ms-store-select-venchi__store-hours-details-group'>
        //         {storeHoursList.map(displayTime => (
        //             <div className='ms-store-select-venchi__store-hours-details' key={JSON.stringify(displayTime)}>
        //                 {displayTime.endDayShort ? (
        //                     <div className='ms-store-select-venchi__store-hours-day'>
        //                         <abbr title={displayTime.startDayFull}>{displayTime.startDayShort}</abbr>–
        //                         <abbr title={displayTime.endDayFull}>{displayTime.endDayShort}</abbr>
        //                     </div>
        //                 ) : (
        //                     <div className='ms-store-select-venchi__store-hours-day'>
        //                         <abbr title={displayTime.startDayFull}>{displayTime.startDayShort}</abbr>
        //                     </div>
        //                 )}
        //                 <div className='ms-store-select-venchi__store-hours-time'>{displayTime.timeRange}</div>
        //             </div>
        //         ))}
        //     </div>
        // );
    }

    // If that fails, fall back to using the OpenFrom and OpenTo properties on the location
    const storeOpenFrom: string | undefined = secondsToTime(location.OpenFrom);
    const storeOpenTo: string | undefined = secondsToTime(location.OpenTo);

    return (
        <div className='ms-store-select-venchi__store-hours-details'>
            {storeOpenFrom}-{storeOpenTo}
        </div>
    );
};

/**
 * Function to return distance measure unit.
 * @param distance - Store location within radius.
 * @param preferredDistanceUnit - Unit configuration.
 * @param isLocationDisabled - Flag to check user location.
 * @returns - Unit of distance.
 */
export const buildDistanceString = (
    distance: number | undefined,
    preferredDistanceUnit: string,
    isLocationDisabled?: boolean
): string | undefined => {
    if (distance === undefined || isLocationDisabled) {
        return undefined;
    } else if (distance < 1) {
        return `(<1 ${preferredDistanceUnit})`;
    }
    return `(${Math.floor(distance)} ${preferredDistanceUnit})`;
};

/**
 * Function to store contact.
 * @param contacts - Store location within radius.
 * @returns - Return primary contact number of store.
 */
const extractPhoneNumber = (contacts: OrgUnitContact[] | undefined): string | undefined => {
    if (!contacts) {
        return undefined;
    }

    const allPhoneContacts = contacts.filter(contact => contact.ContactTypeValue === 1 && !contact.IsPrivate);

    if (ArrayExtensions.hasElements(allPhoneContacts)) {
        const primaryPhoneContact = allPhoneContacts.find(contact => contact.IsPrimary);

        if (primaryPhoneContact) {
            return primaryPhoneContact.Locator;
        }

        return allPhoneContacts[0].Locator;
    }

    return undefined;
};

export function formatAddress(location: OrgUnitLocation): string {
    const addressParts = [];

    // Street and Street Number
    if (location.Street || location.StreetNumber) {
        addressParts.push(`${location.Street || ''} ${location.StreetNumber || ''}`.trim());
    }

    // District and Building Compliment
    const districtBuildingParts = [];
    if (location.DistrictName) {
        districtBuildingParts.push(location.DistrictName);
    }
    if (location.BuildingCompliment) {
        districtBuildingParts.push(location.BuildingCompliment);
    }
    if (districtBuildingParts.length > 0) {
        addressParts.push(districtBuildingParts.join(', '));
    }

    // City, County, State
    const cityCountyStateParts = [];
    if (location.City) {
        cityCountyStateParts.push(location.City);
    }
    if ((location.State || location.StateName) && !(location.Country === 'ITA')) {
        cityCountyStateParts.push(location.State || location.StateName);
    }
    if (cityCountyStateParts.length > 0) {
        addressParts.push(cityCountyStateParts.join(', '));
    }

    // Zipcode
    if (location.Zip) {
        addressParts.push(location.Zip);
    }

    // Country
    if (location.Country) {
        addressParts.push(location.Country);
    }

    return addressParts.join('\n');
}
/**
 * Renders store location line items.
 * @param props - Store location line items props.
 * @returns - HTML.
 */
const storeSelectorLocationLineItem: React.FC<IStoreSelectorLocationLineItemProps> = (props: IStoreSelectorLocationLineItemProps) => {
    const {
        location,
        resources,
        storeHours,
        index,
        isLocationDisabled,
        storePageLinksCountries,
        shouldShowStorePageLinks,
        baseUrl,
        baseClassName
    } = props;

    var storeName: string | undefined = location.OrgUnitName;
    const retailer = storeName?.startsWith('R ') ? true : false;
    if (storeName && retailer) {
        storeName = storeName.slice(2);
    }
    const storeUrl = storeName
        ?.toLowerCase()
        .replace(/[ .&@',]/g, '-')
        .replace(/-+/g, '-');
    const distanceAsString: string | undefined = buildDistanceString(location.Distance, props.preferredDistanceUnit, isLocationDisabled);
    const storeAddress: string = formatAddress(location);
    const phoneNumber: string | undefined = extractPhoneNumber(location.Contacts);
    const displayStoreHours = hasStoreHoursInfo(storeHours, location);
    const parentClassName = baseClassName ? baseClassName : 'ms-store-select-venchi__location-line-item';

    const urlStorePage = (): JSX.Element | null => {
        if (!retailer && shouldShowStorePageLinks && location.Country && storePageLinksCountries?.includes(location.Country)) {
            return (
                <div className={`${parentClassName}-shop-storepageurl-section`}>
                    <span className={`msi msi-storepageurl ${parentClassName}-shop-storepageurl-icon`} />
                    <a
                        className={`${parentClassName}-shop-storepageurl-link`}
                        target='_blank'
                        href={`${baseUrl}${storeUrl}`}
                        rel='noreferrer'
                    >
                        {resources.goToStoreWebPage}
                    </a>
                </div>
            );
        } else {
            return null;
        }
    };

    /* eslint-disable prettier/prettier */
    return (
        <div className={parentClassName}>
            <div className={retailer ? `${parentClassName}-header retailerstore` : `${parentClassName}-header venchistore`}>
                <p className={`${parentClassName}-store-intro`}>
                    <span className={`${parentClassName}-store-index`}>{`${
                        retailer ? resources.storeLabelRetailer : resources.storeLabelVenchi
                    } ${index}`}</span>
                    {distanceAsString ? <span className={`${parentClassName}-store-distance`}> {distanceAsString}</span> : false}
                </p>
                <h3 className={`${parentClassName}-store-name`}>{storeName}</h3>
            </div>
            <div className={`${parentClassName}-content`}>
                <div className={`${parentClassName}-contact-info`}>
                    {storeAddress && (
                        <div className={`${parentClassName}-shop-address-section`}>
                            <span className={`msi msi-map-marker-alt ${parentClassName}-shop-address-glyph-icon`} />
                            <span className={`${parentClassName}-shop-address-text`}>{storeAddress}</span>
                        </div>
                    )}
                    {phoneNumber && (
                        <div className={`${parentClassName}-shop-phone-section`}>
                            <span className={`msi msi-phone ${parentClassName}-shop-phone-icon`} />
                            <span className={`${parentClassName}-shop-phone-text`}>{phoneNumber}</span>
                        </div>
                    )}
                    {urlStorePage()}
                </div>
                {displayStoreHours && (
                    <div className={`${parentClassName}-store-hours`}>
                        <p className={`${parentClassName}-additional-header`}>{resources.storeHoursHeader}</p>
                        {renderStoreHours(storeHours, location, resources)}
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * Store location line item component.
 */
export const StoreSelectorLocationLineItemComponent = storeSelectorLocationLineItem as (
    props: IStoreSelectorLocationLineItemProps
) => JSX.Element;
